import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BlogCard from './components/BlogCard';

const BlogListingPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'case-studies', label: 'Case Studies' },
    { id: 'fire-science', label: 'Fire Science' },
    { id: 'technology', label: 'Technology' },
    { id: 'industry-news', label: 'Industry News' },
    { id: 'best-practices', label: 'Best Practices' }
  ];

  useEffect(() => {
    fetchBlogs();
  }, [selectedCategory, searchQuery]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('blogs')?.select('*')?.eq('status', 'published')?.order('published_at', { ascending: false });

      if (selectedCategory !== 'all') {
        query = query?.eq('category', selectedCategory);
      }

      if (searchQuery) {
        query = query?.ilike('title', `%${searchQuery}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const formattedPosts = data?.map((blog, index) => ({
        id: blog?.id,
        title: blog?.title,
        excerpt: blog?.excerpt,
        category: blog?.category,
        author: blog?.author_name,
        authorRole: blog?.author_role,
        date: new Date(blog?.published_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        readTime: blog?.read_time,
        image: blog?.featured_image,
        alt: `Featured image for ${blog?.title}`,
        featured: index < 2,
        slug: blog?.slug
      })) || [];

      setBlogPosts(formattedPosts);
      setError(null);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError(err?.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = blogPosts;

  const featuredPosts = filteredPosts?.filter(post => post?.featured);
  const regularPosts = filteredPosts?.filter(post => !post?.featured);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center hover:opacity-80 transition-opacity">
              <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
            </button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="flex items-center gap-2">
              
              <Icon name="ArrowLeft" size={16} />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              FireCaseIQ Blog
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Insights, case studies, and best practices in fire investigation and analysis. 
              Stay updated with the latest in fire science and technology.
            </p>

            {/* Search Bar */}
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
              
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories?.map((category) =>
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-smooth ${
              selectedCategory === category?.id ?
              'bg-primary text-primary-foreground' :
              'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'}`
              }>
              
                {category?.label}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg mb-8">
            {error}
          </div>
        )}

        {!loading && !error && filteredPosts?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="FileText" size={64} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-xl text-muted-foreground">
              No blog posts found. Check back soon!
            </p>
          </div>
        )}

        {!loading && !error && filteredPosts?.length > 0 && (
          <>
            {featuredPosts?.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-foreground mb-6">Featured Articles</h2>
                <div className="space-y-6">
                  {featuredPosts?.map((post) => (
                    <BlogCard key={post?.id} post={post} featured={true} />
                  ))}
                </div>
              </section>
            )}

            {regularPosts?.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  {featuredPosts?.length > 0 ? 'More Articles' : 'Latest Articles'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularPosts?.map((post) => (
                    <BlogCard key={post?.id} post={post} featured={false} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Newsletter CTA */}
      <section className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background border-t border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-16 text-center">
          <Icon name="Mail" size={48} className="text-primary mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Stay Updated
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Subscribe to our newsletter for the latest insights in fire investigation and analysis.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            
            <Button size="lg">Subscribe</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogListingPage;